import React, { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import demoProfile from '../assets/DemoImage.jpg'; 

const testimonials = [
  {
    text: "I started using Kumkumadi oil since last month. I see fading of the facial pigmentation. It is a great Ayurvedic product.",
    name: "Anjali S.",
    role: "Verified Buyer",
    review: "⭐⭐⭐⭐⭐"
  },
  {
    text: "The best Ashwagandha capsules I have found online. Purity is visible.",
    name: "Vikram Singh",
    role: "Regular Customer",
    review: "⭐⭐⭐⭐⭐"
  },
  {
    text: "Absolutely love the hair oil. My hair fall has stopped completely.",
    name: "Priya M.",
    role: "Verified Buyer",
    review: "⭐⭐⭐⭐⭐"
  },
  // Add more dummy data if needed to test scrolling
];

const TestimonialSlider: React.FC = () => {
  // State for Mobile Slider
  const [currentIndex, setCurrentIndex] = useState(0);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <div className="bg-[#fffcf1] py-12 md:py-20 overflow-hidden relative">
      
      {/* Title Section */}
      <div className="container mx-auto px-4 text-center mb-8 md:mb-14">
        <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#334a21] mb-3">
          Trusted Reviews: Why India Loves AyushVatika
        </h2>
        <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
          See what our community says about our authentic herbal medicines.
        </p>
        <div className="h-1 w-24 bg-[#334a21] mx-auto rounded-full opacity-20 mt-4"></div>
      </div>

      {/* ========================================= */}
      {/* MOBILE VIEW (Slider with Arrows)          */}
      {/* ========================================= */}
      <div className="block md:hidden relative w-full px-4">
        <div className="relative max-w-[340px] mx-auto">
          {/* Main Card Container */}
          <div className="overflow-hidden rounded-xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out" 
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((item, idx) => (
                <div key={idx} className="w-full flex-shrink-0 px-1">
                  <TestimonialCard data={item} isMobile={true} />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={prevSlide}
            className="absolute top-1/2 -left-4 -translate-y-1/2 bg-white shadow-lg text-[#334a21] p-2 rounded-full hover:bg-gray-100 border border-gray-100 z-10"
            aria-label="Previous review"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <button 
            onClick={nextSlide}
            className="absolute top-1/2 -right-4 -translate-y-1/2 bg-white shadow-lg text-[#334a21] p-2 rounded-full hover:bg-gray-100 border border-gray-100 z-10"
            aria-label="Next review"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center mt-6 gap-2">
          {testimonials.map((_, idx) => (
            <div 
              key={idx} 
              className={`h-2 w-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-[#334a21]' : 'bg-gray-300'
              }`} 
            />
          ))}
        </div>
      </div>

      {/* ========================================= */}
      {/* DESKTOP VIEW (Infinite Auto-Scroll)       */}
      {/* ========================================= */}
      <div className="hidden md:block w-full">
        <div className="flex w-max group">
          <div className="flex animate-scroll-slow group-hover:paused">
            {/* Set 1 */}
            {testimonials.map((item, idx) => (
              <TestimonialCard key={`original-${idx}`} data={item} isMobile={false} />
            ))}
            {/* Set 2 */}
            {testimonials.map((item, idx) => (
              <TestimonialCard key={`duplicate-${idx}`} data={item} isMobile={false} />
            ))}
          </div>
        </div>
      </div>

      {/* CSS Animation for Desktop */}
      <style>{`
        @keyframes scrollLeft {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll-slow {
          animation: scrollLeft 60s linear infinite;
        }
        .group-hover\\:paused:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

// --- Card Component ---
interface TestimonialCardProps {
  data: {
    text: string;
    name: string;
    role: string;
    review: string;
  };
  isMobile: boolean;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ data, isMobile }) => {
  return (
    <div 
      className={`
        bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 flex flex-col
        ${isMobile ? 'w-full h-[320px]' : 'w-[380px] h-full mx-6 transform hover:-translate-y-1'}
      `}
    >
      <div className="h-1.5 w-full bg-[#334a21]"></div>
      
      {/* Adjusted padding: p-5 for mobile, p-8 for desktop */}
      <div className={`${isMobile ? 'p-5' : 'p-8'} flex flex-col h-full justify-between`}>
        
        <div className="flex justify-between items-start mb-3 md:mb-4">
          <div className="text-yellow-500 text-xs md:text-sm tracking-wide bg-yellow-50 px-2 py-1 rounded-md font-medium">
            {data.review}
          </div>
        </div>

        {/* Adjusted Font Size: text-base for mobile, text-lg for desktop */}
        <p className={`text-gray-700 leading-relaxed mb-4 md:mb-6 font-serif italic opacity-90 ${isMobile ? 'text-base line-clamp-5' : 'text-lg'}`}>
          "{data.text}"
        </p>

        <div className="flex items-center pt-3 md:pt-4 border-t border-gray-100 mt-auto">
          <img 
            src={demoProfile} 
            alt={`Review by ${data.name}`} 
            className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover mr-3 md:mr-4 ring-2 ring-gray-100"
          />
          <div>
            <h4 className="text-sm md:text-base font-bold text-[#334a21]">{data.name}</h4>
            <p className="text-[10px] md:text-xs text-gray-400 font-medium uppercase tracking-wider">{data.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialSlider;